import { useQuery } from '@tanstack/react-query'
import { supabase, isDemoMode } from '@/lib/supabase'
import { DEMO_KPIS, DEMO_PARCELS, DEMO_PROGRESS_BY_DISCIPLINE, DEMO_ALERTS } from '@/lib/demoData'
import type { DashboardKPIs } from '@/types'

export function useDashboardKPIs() {
  return useQuery<DashboardKPIs>({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_KPIS
      const { data, error } = await supabase.rpc('get_dashboard_kpis')
      if (error) throw error
      return data as DashboardKPIs
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useParcelProgress() {
  return useQuery({
    queryKey: ['dashboard', 'parcel-progress'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_PARCELS.map(p => ({
        parcel_id:     p.id,
        parcel_name:   p.display_name,
        parcel_number: p.parcel_number,
        progress_pct:  p.progress_pct ?? 0,
      }))
      const { data, error } = await supabase
        .from('parcels')
        .select('id, parcel_number, display_name')
        .order('parcel_number')
      if (error) throw error
      return (data as any[]).map(p => ({ parcel_id: p.id, parcel_name: p.display_name, parcel_number: p.parcel_number, progress_pct: 0 }))
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useDisciplineProgress() {
  return useQuery({
    queryKey: ['dashboard', 'discipline-progress'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_PROGRESS_BY_DISCIPLINE
      const { data, error } = await supabase
        .from('activity_instances')
        .select('status, family:activity_families(discipline)')
      if (error) throw error
      const grouped: Record<string, { discipline: string; not_started: number; in_progress: number; completed: number; blocked: number; total: number; pct: number }> = {}
      for (const row of (data as any[])) {
        const disc = (row as any).family?.discipline ?? 'Otros'
        if (!grouped[disc]) grouped[disc] = { discipline: disc, not_started: 0, in_progress: 0, completed: 0, blocked: 0, total: 0, pct: 0 }
        grouped[disc].total++
        const status = (row as any).status
        if (status === 'NOT_STARTED') grouped[disc].not_started++
        else if (status === 'IN_PROGRESS') grouped[disc].in_progress++
        else if (status === 'COMPLETED') grouped[disc].completed++
        else if (status === 'BLOCKED') grouped[disc].blocked++
      }
      return Object.values(grouped)
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useAlerts() {
  return useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_ALERTS
      return []
    },
    staleTime: 60 * 1000,
  })
}

// Legacy combined hook for backwards compatibility
export function useDashboard() {
  const kpis       = useDashboardKPIs()
  const parcel     = useParcelProgress()
  const discipline = useDisciplineProgress()
  const alerts     = useAlerts()

  return {
    kpis:               kpis.data ?? null,
    parcelProgress:     parcel.data ?? [],
    disciplineProgress: discipline.data ?? [],
    alerts:             alerts.data ?? [],
    isLoading:          kpis.isLoading || parcel.isLoading,
    error:              kpis.error || parcel.error,
    refetch:            kpis.refetch,
  }
}
